import React, { useEffect, useRef } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UserDocumentProvider } from './contexts/UserDocumentContext';
import { DropdownProvider } from './contexts/DropdownContext';
import { AccountProvider } from './contexts/AccountContext';
import { AccountTypeProvider } from './contexts/AccountTypeContext';
import { LoadingProvider, useLoading } from './contexts/LoadingContext';
import { LogoProvider, useLogo } from './contexts/LogoContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { OrderProvider } from './contexts/OrderContext';
import { PositionProvider } from './contexts/PositionContext';
import { ChartPositionProvider } from './contexts/ChartPositionContext';
import { ToastProvider } from './contexts/ToastContext';
import { SponsorProvider } from './contexts/SponsorContext';
import { AITradingProvider } from './contexts/AITradingContext';
import { BackendAITradingProvider } from './contexts/BackendAITradingContext';
import { AIStrategyPersistenceProvider } from './contexts/AIStrategyPersistenceContext';
import { FullChartViewProvider } from './contexts/FullChartViewContext';
import { PanelPositionProvider } from './contexts/PanelPositionContext';
import { PriceInputProvider } from './contexts/PriceInputContext';
import { TradingErrorProvider } from './contexts/TradingErrorContext';
import Layout from './components/Layout';
import ReorderableLayout from './components/ReorderableLayout';
import serviceManager from './services/serviceManager';
import memoryMonitor from './utils/memoryMonitor';
// import { toggleFullscreen } from './utils/fullscreenUtils';

import Analytics from './pages/Analytics';
import Portfolio from './pages/Portfolio';
import Settings from './pages/Settings';
import AccountDetails from './pages/AccountDetails';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ProtectedRoute from './components/ProtectedRoute';
import PendingApprovalBlock from './components/PendingApprovalBlock';
import Loader from './components/Loader';


const AppRoutes: React.FC = () => {
  const { isLoading, isInitializing, loadingProgress, loadingMessage } = useLoading();
  const { user } = useAuth();

  // Global cleanup on unmount
  useEffect(() => {
    return () => {
      // Force cleanup all services when app unmounts
      serviceManager.forceCleanup();
      // Stop memory monitoring
      memoryMonitor.stopMonitoring();
    };
  }, []);

  // Note: Automatic fullscreen on app load was removed because browsers require user interaction
  // before allowing fullscreen mode. Users can still enter fullscreen by:
  // 1. Clicking the fullscreen button in the header
  // 2. Pressing F11 key
  // 3. Using the fullscreen toggle in the layout

  // Show initialization loader for unauthenticated users
  if (!user && isInitializing) {
    return (
      <Loader 
        fullScreen={true} 
        title="Live Trading Studio"
        subtitle="Initializing..."
        progress={loadingProgress}
        showProgress={true}
      />
    );
  }

  // Show main loader for authenticated users
  if (isLoading) {
    return (
      <Loader 
        fullScreen={true} 
        title="Live Trading Studio"
        subtitle={loadingMessage}
        progress={loadingProgress}
        showProgress={true}
      />
    );
  }

  return (
    <>
      <Routes>
        {/* Public routes - no authentication required */}
        <Route 
          path="/login" 
          element={
            <ProtectedRoute requireAuth={false}>
              <Login />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/register" 
          element={
            <ProtectedRoute requireAuth={false}>
              <Register />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/forgot-password" 
          element={
            <ProtectedRoute requireAuth={false}>
              <ForgotPassword />
            </ProtectedRoute>
          } 
        />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        
        {/* Protected routes - authentication required */}
        <Route
          path="/"
          element={
            <ProtectedRoute requireAuth={true}>
              <PendingApprovalBlock>
                <Layout>
                  <Navigate to="/miai" replace />
                </Layout>
              </PendingApprovalBlock>
            </ProtectedRoute>
          }
        />
        
        {/* Separate routes for the new pages */}
        <Route
          path="/analytics"
          element={
            <ProtectedRoute requireAuth={true}>
              <PendingApprovalBlock>
                <Layout>
                  <Analytics />
                </Layout>
              </PendingApprovalBlock>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/portfolio"
          element={
            <ProtectedRoute requireAuth={true}>
              <PendingApprovalBlock>
                <Layout>
                  <Portfolio />
                </Layout>
              </PendingApprovalBlock>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/settings"
          element={
            <ProtectedRoute requireAuth={true}>
              <PendingApprovalBlock>
                <Layout>
                  <Settings />
                </Layout>
              </PendingApprovalBlock>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/miai"
          element={
            <ProtectedRoute requireAuth={true}>
              <PendingApprovalBlock>
                <Layout>
                  <ReorderableLayout />
                </Layout>
              </PendingApprovalBlock>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/livetradingstudio"
          element={
            <ProtectedRoute requireAuth={true}>
              <PendingApprovalBlock>
                <Layout>
                  <ReorderableLayout />
                </Layout>
              </PendingApprovalBlock>
            </ProtectedRoute>
          }
        />
        
        
        <Route
          path="/account-details"
          element={
            <ProtectedRoute requireAuth={true}>
              <PendingApprovalBlock>
                <Layout>
                  <AccountDetails />
                </Layout>
              </PendingApprovalBlock>
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <UserDocumentProvider>
        <LogoProvider>
          <LoadingProvider>
            <AccountProvider>
              <AccountTypeProvider>
                <DropdownProvider>
                  <ThemeProvider>
                    <SponsorProvider>
                      <FullChartViewProvider>
                        <PanelPositionProvider>
                          <PositionProvider>
                            <ChartPositionProvider>
                              <AITradingProvider>
                                <BackendAITradingProvider>
                                  <AIStrategyPersistenceProvider>
                                    <PriceInputProvider>
                                      <TradingErrorProvider>
                                        <ToastProvider>
                                          <OrderProvider>
                                            <AppRoutes />
                                          </OrderProvider>
                                        </ToastProvider>
                                      </TradingErrorProvider>
                                    </PriceInputProvider>
                                  </AIStrategyPersistenceProvider>
                                </BackendAITradingProvider>
                              </AITradingProvider>
                            </ChartPositionProvider>
                          </PositionProvider>
                        </PanelPositionProvider>
                      </FullChartViewProvider>
                    </SponsorProvider>
                  </ThemeProvider>
                </DropdownProvider>
              </AccountTypeProvider>
            </AccountProvider>
          </LoadingProvider>
        </LogoProvider>
      </UserDocumentProvider>
    </AuthProvider>
  );
};

export default App;
